import { useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '../ui/Button';
import { PageThumbnail } from './PageThumbnail';
import { pdfPageUrl } from '../../api/files';

// Хардлимит API: `POST /pages/select` принимает до 10 страниц за раз.
export const MAX_SELECTED_PAGES = 10;

export interface PagePickerProps {
  pdfGuid: string;
  totalPages: number;
  onConfirm: (pages: number[]) => void;
  loading?: boolean;
}

export function PagePicker({ pdfGuid, totalPages, onConfirm, loading }: PagePickerProps) {
  // По умолчанию выбираем первые MAX_SELECTED_PAGES страниц — пользователь сам
  // снимет лишнее / добавит другие. Выделить «все» нельзя, если оригинал больше
  // 10 страниц.
  const initialPages = useMemo(() => {
    const count = Math.min(totalPages, MAX_SELECTED_PAGES);
    return new Set(Array.from({ length: count }, (_, i) => i + 1));
  }, [totalPages]);

  const [selected, setSelected] = useState<Set<number>>(initialPages);

  const toggle = (index: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        if (next.size >= MAX_SELECTED_PAGES) return prev;
        next.add(index);
      }
      return next;
    });
  };

  const selectFirst10 = () => {
    const count = Math.min(totalPages, MAX_SELECTED_PAGES);
    setSelected(new Set(Array.from({ length: count }, (_, i) => i + 1)));
  };

  const clear = () => setSelected(new Set());

  const count = selected.size;
  const atLimit = count >= MAX_SELECTED_PAGES;
  const canSubmit = count > 0 && count <= MAX_SELECTED_PAGES;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-text-muted">
          Выбрано:{' '}
          <span className="font-medium text-text">{count}</span> из{' '}
          {totalPages}{' '}
          <span className="text-xs">
            (максимум {MAX_SELECTED_PAGES} за раз)
          </span>
        </p>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={selectFirst10}>
            Первые {Math.min(totalPages, MAX_SELECTED_PAGES)}
          </Button>
          <Button variant="ghost" size="sm" onClick={clear}>
            Снять выбор
          </Button>
        </div>
      </div>

      {atLimit && (
        <div className="flex items-start gap-2 rounded-[14px] bg-bg-soft px-3.5 py-2 text-sm text-text">
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-brand-primary" />
          <span>
            Выбрано максимально допустимое количество страниц. Чтобы добавить
            другую — сначала снимите одну из выбранных.
          </span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((i) => {
          const isSelected = selected.has(i);
          const disabledByLimit = !isSelected && atLimit;
          return (
            <PageThumbnail
              key={i}
              index={i}
              src={pdfPageUrl(pdfGuid, i)}
              selected={isSelected}
              disabled={disabledByLimit}
              onToggle={toggle}
            />
          );
        })}
      </div>

      <div className="sticky bottom-2 flex justify-end">
        <Button
          onClick={() => onConfirm(Array.from(selected).sort((a, b) => a - b))}
          disabled={!canSubmit}
          loading={loading}
          size="lg"
        >
          Обработать выбранные ({count})
        </Button>
      </div>
    </div>
  );
}
